import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { CheckCircle2, XCircle, ArrowLeft, Loader2, Award } from "lucide-react";

export default function Exam() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [optionOrders, setOptionOrders] = useState({});
  const [courseTitle, setCourseTitle] = useState("");
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const start = async () => {
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post(`/learning/courses/${slug}/exam/start`);
      setQuestions(data.questions);
      setOptionOrders(Object.fromEntries(data.questions.map((q) => [q.id, q.option_order])));
      setCourseTitle(data.course_title);
      setAnswers({});
    } catch (err) { toast.error(formatApiError(err)); }
    setLoading(false);
  };
  useEffect(() => { start(); }, [slug]);

  const submit = async () => {
    if (Object.keys(answers).length < questions.length) {
      toast.error(`Answer all ${questions.length} questions first.`);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/learning/courses/${slug}/exam/submit`, {
        answers, option_orders: optionOrders,
      });
      setResult(data);
      if (data.passed) toast.success(`Certified! +score boost applied.`);
      else toast.error(`You scored ${data.score_pct}% — need 80%. Try again.`);
    } catch (err) { toast.error(formatApiError(err)); }
    setSubmitting(false);
  };

  if (loading) return <div className="min-h-screen bg-black text-white"><Navbar /><div className="p-12 text-sm text-neutral-500">Loading exam…</div></div>;

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 md:px-12 py-10">
        <Link to={`/courses/${slug}`} className="inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-white mb-4">
          <ArrowLeft size={12} /> Back to course
        </Link>
        <div className="text-overline mb-2">Certification Exam</div>
        <h1 className="font-display text-3xl font-medium tracking-tight mb-2">{courseTitle}</h1>
        <p className="text-sm text-neutral-400 mb-8">{questions.length} questions · 80% to pass · unlimited retakes</p>

        {!result && (
          <div className="space-y-6">
            {questions.map((q, qi) => (
              <div key={q.id} data-testid={`exam-question-${q.id}`} className="border border-white/5 bg-[#0c0c0c] p-6">
                <div className="text-xs font-mono-tech uppercase tracking-widest text-amber-500 mb-2">Question {qi + 1} of {questions.length}</div>
                <div className="text-base text-white mb-4">{q.prompt}</div>
                <div className="space-y-2">
                  {q.options.map((opt, i) => {
                    const sel = answers[q.id] === i;
                    return (
                      <button key={i} onClick={() => setAnswers({ ...answers, [q.id]: i })}
                        data-testid={`exam-option-${q.id}-${i}`}
                        className={`w-full text-left px-4 py-3 text-sm border transition-colors ${sel ? "border-amber-500 bg-amber-500/10 text-white" : "border-white/10 text-neutral-300 hover:border-white/25"}`}>
                        <span className="font-mono-tech text-xs text-neutral-500 mr-3">{String.fromCharCode(65 + i)}.</span>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            <button onClick={submit} disabled={submitting}
              data-testid="submit-exam-btn"
              className="w-full px-6 py-4 bg-amber-500 text-black font-semibold uppercase tracking-widest text-sm hover:brightness-110 disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {submitting && <Loader2 size={14} className="animate-spin" />}
              Submit Exam
            </button>
          </div>
        )}

        {result && (
          <div data-testid="exam-result" className={`border ${result.passed ? "border-emerald-500/40 bg-emerald-500/5" : "border-rose-500/40 bg-rose-500/5"} p-8 mb-6`}>
            <div className="flex items-center gap-3 mb-3">
              {result.passed ? <CheckCircle2 size={28} className="text-emerald-400" /> : <XCircle size={28} className="text-rose-400" />}
              <div>
                <h2 className="font-display text-2xl font-medium">{result.passed ? "Certified!" : "Try Again"}</h2>
                <div className="font-mono-tech text-xs uppercase tracking-widest text-neutral-400">
                  Score: <span className={result.passed ? "text-emerald-400" : "text-rose-400"}>{result.score_pct}%</span> · {result.correct} / {result.total} correct
                </div>
              </div>
            </div>
            {result.passed && (
              <div className="text-sm text-neutral-300 mb-4 flex items-center gap-2">
                <Award size={14} className="text-amber-500" />
                Certification awarded · score boost applied · added to your Passport &amp; Achievement Vault.
              </div>
            )}
            <div className="flex items-center gap-3 mt-5">
              {result.passed ? (
                <button onClick={() => navigate("/dashboard")} data-testid="exam-go-dashboard"
                  className="px-4 py-2.5 bg-amber-500 text-black text-xs font-semibold uppercase tracking-widest hover:brightness-110">
                  Back to Dashboard →
                </button>
              ) : (
                <button onClick={start} data-testid="exam-retry"
                  className="px-4 py-2.5 bg-amber-500 text-black text-xs font-semibold uppercase tracking-widest hover:brightness-110">
                  Retake Exam
                </button>
              )}
              <Link to={`/courses/${slug}`} className="text-xs text-neutral-400 hover:text-white font-mono-tech uppercase tracking-widest">
                Review lessons
              </Link>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-3 mt-8">
            <div className="text-xs uppercase tracking-widest text-neutral-500 font-mono-tech mb-2">Answer Review</div>
            {result.results.map((r, i) => (
              <div key={r.question_id} data-testid={`exam-review-${r.question_id}`}
                className={`border ${r.is_correct ? "border-emerald-500/20" : "border-rose-500/20"} p-4 bg-[#0c0c0c]`}>
                <div className="flex items-start gap-2 mb-2">
                  {r.is_correct ? <CheckCircle2 size={14} className="text-emerald-400 mt-0.5 shrink-0" /> : <XCircle size={14} className="text-rose-400 mt-0.5 shrink-0" />}
                  <div className="text-sm text-white">{i + 1}. {r.prompt}</div>
                </div>
                <div className="ml-6 text-xs">
                  {!r.is_correct && r.your_option && (
                    <div className="text-rose-300 mb-1">Your answer: {r.your_option}</div>
                  )}
                  <div className="text-emerald-300">Correct: {r.correct_option}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
